import React from "react";
import { createPortal } from "react-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useNotificationAction } from "../../hooks/useNotificationAction";

export default function NotificationModal({
    open,
    onClose,
    items = [],
    onMarkAsRead,
    onMarkAllAsRead,
}) {
    // ✅ Call hooks at the top level, not inside conditional
    const { getActionForNotification, handleNotificationClick } = useNotificationAction();
    const unreadCount = items.filter((item) => !item.read).length;

    if (!open) return null;

    const formatTime = (date) => {
        if (!date) return '';
        const now = new Date();
        const notifDate = new Date(date);
        const diffMins = Math.floor((now - notifDate) / 60000);
        const diffHours = Math.floor((now - notifDate) / 3600000);
        const diffDays = Math.floor((now - notifDate) / 86400000);

        if (diffMins < 1) return 'now';
        if (diffMins < 60) return `${diffMins}m`;
        if (diffHours < 24) return `${diffHours}h`;
        if (diffDays < 7) return `${diffDays}d`;
        return notifDate.toLocaleDateString();
    };

    const handleNotificationItemClick = (item) => {
        // Mark as read
        if (onMarkAsRead) {
            onMarkAsRead(item.id);
        }

        // Navigate if action exists for this type
        const hasNavigated = handleNotificationClick(item);

        // Close modal after action
        if (hasNavigated) {
            onClose();
        }
    };

    const modalContent = (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 z-[70]"
                onClick={onClose}
            />

            {/* Dropdown from top-right */}
            <div
                role="dialog"
                aria-modal="true"
                data-modal-back="true"
                className="fixed top-16 right-4 z-[80] w-96 bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[500px] border border-gray-200"
            >
                {/* Header */}
                <div className="px-5 py-4 flex items-center justify-between border-b border-gray-100">
                    <h2 className="text-base font-semibold text-gray-900">Notifications</h2>
                    <button
                        data-modal-close
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                        aria-label="Close"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="overflow-y-auto flex-1">
                    {items.length === 0 ? (
                        <div className="text-center py-12 px-5">
                            <p className="text-gray-500 text-sm">No notifications</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {items.map((item) => {
                                const action = getActionForNotification(item.type);
                                const ActionIcon = action?.icon;

                                return (
                                    <div
                                        key={item.id}
                                        onClick={() => handleNotificationItemClick(item)}
                                        className={`px-5 py-3 transition-colors cursor-pointer hover:bg-gray-50 group ${item.read ? 'bg-white' : 'bg-blue-50'
                                            }`}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex items-start gap-3 flex-1 min-w-0">
                                                {ActionIcon && (
                                                    <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                                                        <ActionIcon className={`h-4 w-4 ${action.color}`} />
                                                    </div>
                                                )}

                                                <div className="flex-1 min-w-0">
                                                    <p className="text-gray-900 text-sm font-medium leading-snug line-clamp-2">
                                                        {item.message}
                                                    </p>

                                                    {action && (
                                                        <div className="flex items-center gap-1 mt-2">
                                                            <span className={`text-xs font-medium ${action.color}`}>
                                                                {action.label}
                                                            </span>
                                                            <svg className={`w-3 h-3 ${action.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                            </svg>
                                                        </div>
                                                    )}

                                                    <div className="flex items-center gap-2 mt-2">
                                                        <span className="text-xs text-gray-500">
                                                            {formatTime(item.createdAt)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {!item.read && (
                                                <span className="w-2.5 h-2.5 rounded-full bg-blue-500 flex-shrink-0 mt-1" />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                        {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
                    </span>
                    <button
                        type="button"
                        onClick={onMarkAllAsRead}
                        disabled={unreadCount === 0}
                        className="text-sm font-medium text-blue-600 transition hover:text-blue-700 disabled:cursor-not-allowed disabled:text-gray-400"
                    >
                        Mark all as read
                    </button>
                </div>
            </div>
        </>
    );

    return createPortal(modalContent, document.body);
}
